import cx from 'clsx';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as AIReportDarkIcon } from '@assets/svg/reports-icons/action-item-report-dark.svg';
import { ReactComponent as AIReportIcon } from '@assets/svg/reports-icons/action-item-report.svg';
import { ReactComponent as ArchivedAIReportDarkIcon } from '@assets/svg/reports-icons/archived-action-item-report-dark.svg';
import { ReactComponent as ArchivedAIReportIcon } from '@assets/svg/reports-icons/archived-action-item-report.svg';
import { ReactComponent as CostSavingsReportDarkIcon } from '@assets/svg/reports-icons/cost-savings-report-dark.svg';
import { ReactComponent as CostSavingsReportIcon } from '@assets/svg/reports-icons/cost-savings-report.svg';
import { ReactComponent as DoubleArrow } from '@assets/svg/reports-icons/double-arrow.svg';
import { ReactComponent as FailureReportDarkIcon } from '@assets/svg/reports-icons/failure-report-dark.svg';
import { ReactComponent as FailureReportIcon } from '@assets/svg/reports-icons/failure-report.svg';
import { ReactComponent as HardwareOverviewReportDarkIcon } from '@assets/svg/reports-icons/hardware-overview-report-dark.svg';
import { ReactComponent as HardwareOverviewReportIcon } from '@assets/svg/reports-icons/hardware-overview-report.svg';
import { ReactComponent as OverallMetricsReportDarkIcon } from '@assets/svg/reports-icons/overall-metrics-report-dark.svg';
import { ReactComponent as OverallMetricsReportIcon } from '@assets/svg/reports-icons/overall-metrics-report.svg';
import { ReactComponent as EngagementReportDarkIcon } from '@assets/svg/reports-icons/site-engagement-report-dark.svg';
import { ReactComponent as EngagementReportIcon } from '@assets/svg/reports-icons/site-engagement-report.svg';
import { ReactComponent as MonitoringReportDarkIcon } from '@assets/svg/reports-icons/site-monitoring-report-dark.svg';
import { ReactComponent as MonitoringReportIcon } from '@assets/svg/reports-icons/site-monitoring-report.svg';
import { ReactComponent as UserListDarkIcon } from '@assets/svg/reports-icons/user-list-dark.svg';
import { ReactComponent as UserListIcon } from '@assets/svg/reports-icons/user-list.svg';

import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import { selectAppRtl } from '@store/useAppStore';
import { selectAppPersistDarkMode } from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import style from './style.module.scss';

interface IReport {
  action: string;
  title: string;
  description: string;
  btnText: string;
  toPage: string;
  icon: string;
  locationsType: string;
  fileFormat: {
    type: string;
    downloadFormat: string;
  };
  extraFileFormat?: {
    type: string;
    downloadFormat: string;
  };
  closedAiFilter?: boolean;
}

const lightReportsIcons: { [key: string]: React.JSX.Element } = {
  'ai-report': <AIReportIcon />,
  'archived-ai-report': <ArchivedAIReportIcon />,
  'cost-savings-report': <CostSavingsReportIcon />,
  'failure-report': <FailureReportIcon />,
  'site-engagement-report': <EngagementReportIcon />,
  'site-monitoring-report': <MonitoringReportIcon />,
  'hardware-overview-report': <HardwareOverviewReportIcon />,
  'user-list': <UserListIcon />,
  'overall-metrics-report': <OverallMetricsReportIcon />,
};

const darkReportsIcons: { [key: string]: React.JSX.Element } = {
  'ai-report': <AIReportDarkIcon />,
  'archived-ai-report': <ArchivedAIReportDarkIcon />,
  'cost-savings-report': <CostSavingsReportDarkIcon />,
  'failure-report': <FailureReportDarkIcon />,
  'site-engagement-report': <EngagementReportDarkIcon />,
  'site-monitoring-report': <MonitoringReportDarkIcon />,
  'hardware-overview-report': <HardwareOverviewReportDarkIcon />,
  'user-list': <UserListDarkIcon />,
  'overall-metrics-report': <OverallMetricsReportDarkIcon />,
};

const Report = ({ report }: { report: IReport }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId } = useParams();
  const { pathname } = useLocation();
  const rtl = selectAppRtl();

  const { setActiveModal } = useModalActions();

  const darkMode = selectAppPersistDarkMode();

  const reportIcons = darkMode ? darkReportsIcons : lightReportsIcons;

  const extraFileFormat = report.extraFileFormat ? ` / ${report.extraFileFormat.downloadFormat}` : '';

  const handleClick = () => {
    if (report.toPage) {
      navigate(`/customer/${customerId}/reports/${report.toPage}`, { state: { prevPage: pathname } });
    } else {
      setActiveModal({ name: MODAL_NAMES.locationSelectModal, options: { report } });
    }
  };

  return (
    <>
      <div className={style.reportItem}>
        <div className={style.reportItemBlock}>
          <div>{reportIcons[report.icon]}</div>
          <div className={style.reportItemTitle}>{t(report.title)}</div>
          <p className={style.reportItemDescription}>{t(report.description)}</p>
        </div>
        <div className={cx(style.reportItemDownload, { [style.reportItemToPage]: report.toPage })}>
          <Button
            type="secondary"
            text={t(report.btnText)}
            onClick={handleClick}
            rightElement={
              report.toPage ? <DoubleArrow className={cx({ [style.arrowIconBack]: rtl })} /> : undefined
            }
          />
          {!report.toPage && (
            <span className={cx(style.reportItemDownloadInfo, { [style.reportItemDownloadInfoRtl]: rtl })}>
              {t(`.${report.fileFormat.downloadFormat}${extraFileFormat} file`)}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

export default Report;
